import React from 'react';
import PropTypes from 'prop-types';
import cn from '../utils/cn';
import styles from './RangeSlider.module.css';

function clamp(n, lo, hi) {
  return Math.min(hi, Math.max(lo, n));
}

const formatCurrency = v => `R$ ${Number(v).toLocaleString('pt-BR')}`;

const RangeSlider = React.forwardRef(function RangeSlider(
  {
    min = 0,
    max = 10000,
    value,
    onChange,
    step = 1,
    disabled,
    currency,
    formatLabel,
    className,
    ...rest
  },
  ref
) {
  const formatValue = formatLabel || (currency ? formatCurrency : String);
  const railRef = React.useRef(null);
  const draggingRef = React.useRef(null);

  const propLo = value ? clamp(value[0], min, max) : min;
  const propHi = value ? clamp(value[1], min, max) : max;

  const [internal, setInternal] = React.useState([propLo, propHi]);
  const internalRef = React.useRef(internal);
  internalRef.current = internal;

  const [active, setActive] = React.useState(null);
  const [hover, setHover] = React.useState(null);

  React.useEffect(() => {
    if (!draggingRef.current) setInternal([propLo, propHi]);
  }, [propLo, propHi]);

  const span = max - min || 1;
  const [lo, hi] = internal;
  const loPct = ((lo - min) / span) * 100;
  const hiPct = ((hi - min) / span) * 100;

  const valueFromClientX = clientX => {
    const rect = railRef.current.getBoundingClientRect();
    const ratio = clamp((clientX - rect.left) / (rect.width || 1), 0, 1);
    const snapped = Math.round((min + ratio * span) / step) * step;
    return clamp(snapped, min, max);
  };

  const commit = (which, next) => {
    const cur = internalRef.current;
    const out =
      which === 'lo'
        ? [Math.min(next, cur[1]), cur[1]]
        : [cur[0], Math.max(next, cur[0])];
    internalRef.current = out;
    setInternal(out);
    if (onChange) onChange(out);
  };

  const onPointerDown = e => {
    if (disabled) return;
    e.preventDefault();
    const next = valueFromClientX(e.clientX);
    const cur = internalRef.current;
    const which =
      Math.abs(next - cur[0]) <= Math.abs(next - cur[1]) ? 'lo' : 'hi';
    draggingRef.current = which;
    setActive(which);
    setHover(null);
    e.currentTarget.setPointerCapture(e.pointerId);
    commit(which, next);
  };

  const onPointerMove = e => {
    if (!draggingRef.current) return;
    commit(draggingRef.current, valueFromClientX(e.clientX));
  };

  const onPointerUp = e => {
    if (!draggingRef.current) return;
    draggingRef.current = null;
    setActive(null);
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const onMouseMove = e => {
    if (disabled || draggingRef.current) return;
    if (e.target.closest('button')) {
      setHover(null);
      return;
    }
    const rect = railRef.current.getBoundingClientRect();
    const pct = clamp((e.clientX - rect.left) / (rect.width || 1), 0, 1) * 100;
    setHover({ pct, value: valueFromClientX(e.clientX) });
  };

  const onMouseLeave = () => setHover(null);

  const onKeyDown = which => e => {
    if (disabled) return;
    let delta = 0;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') delta = -step;
    else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') delta = step;
    if (!delta) return;
    e.preventDefault();
    const cur = internalRef.current;
    commit(which, clamp((which === 'lo' ? cur[0] : cur[1]) + delta, min, max));
  };

  return (
    <div className={cn(styles.root, className)} ref={ref} {...rest}>
      <div
        className={cn(styles.track, disabled && styles.disabled)}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}>
        <div className={styles.rail} ref={railRef}>
          <div
            className={styles.fill}
            style={{ left: `${loPct}%`, width: `${hiPct - loPct}%` }}
          />
          {hover && (
            <span className={styles.preview} style={{ left: `${hover.pct}%` }}>
              {formatValue(hover.value)}
            </span>
          )}
          <button
            type='button'
            className={cn(styles.thumb, active === 'lo' && styles.active)}
            style={{ left: `${loPct}%` }}
            role='slider'
            aria-valuemin={min}
            aria-valuemax={hi}
            aria-valuenow={lo}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            onKeyDown={onKeyDown('lo')}>
            <span className={styles.tooltip}>{formatValue(lo)}</span>
          </button>
          <button
            type='button'
            className={cn(styles.thumb, active === 'hi' && styles.active)}
            style={{ left: `${hiPct}%` }}
            role='slider'
            aria-valuemin={lo}
            aria-valuemax={max}
            aria-valuenow={hi}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            onKeyDown={onKeyDown('hi')}>
            <span className={styles.tooltip}>{formatValue(hi)}</span>
          </button>
        </div>
      </div>
    </div>
  );
});

RangeSlider.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  value: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func,
  step: PropTypes.number,
  disabled: PropTypes.bool,
  currency: PropTypes.bool,
  formatLabel: PropTypes.func,
  className: PropTypes.string
};

export default RangeSlider;
